import { useCallback, useState } from 'react';
import { showErrorAlert } from 'utils/AlertUtils';
import { setMerchantLabels } from 'utils/labelApi';

/**
 * Label assignment for the Edit Merchant form (RAT-1689).
 *
 * Labels are saved by their own endpoint, not as part of the merchant payload,
 * so they are held here rather than in react-hook-form. Kept out of the form
 * component because MerchantAddEdit.jsx is already past the max-lines limit.
 *
 * `hydrate` takes the label objects from the loaded merchant: the ids drive the
 * picker, and the objects are also the baseline for "did this change", so an
 * untouched form never issues a needless write.
 */
const useMerchantLabels = (merchantId, canAssign = true) => {
  const [labelIds, setLabelIds] = useState([]);
  const [initialLabels, setInitialLabels] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const hydrate = useCallback((labels) => {
    const list = labels || [];
    setInitialLabels(list);
    setLabelIds(list.map((label) => label.id));
  }, []);

  const persist = useCallback(async () => {
    if (!merchantId || !canAssign) return true;

    const before = [...initialLabels.map((label) => label.id)].sort();
    const after = [...labelIds].sort();
    if (before.join(',') === after.join(',')) return true;

    setIsSaving(true);
    try {
      await setMerchantLabels(merchantId, labelIds);
      return true;
    } catch (error) {
      // The merchant itself already saved, so say so plainly - the user must not
      // read this as "nothing was saved".
      showErrorAlert(
        error?.message ||
          error?.error?.message ||
          'Merchant saved, but its labels could not be updated',
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [merchantId, canAssign, initialLabels, labelIds]);

  return { labelIds, setLabelIds, initialLabels, hydrate, persist, isSaving };
};

export default useMerchantLabels;
